import React, { useState, useEffect, useCallback } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  DataGridPro,
  LicenseInfo,
  jaJP,
} from '@mui/x-data-grid-pro';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Navbar from '../../components/layout/Navbar'
import HeadLine1 from '../common/HeadLine1.js'
import HeadLine2 from '../common/HeadLine2.js'
import StandardBox from '../common/StandardBox.js'
import CountBox from '../common/CountBox.js'
import ButtonStack from '../common/ButtonStack.js'
import SearchTextField from '../common/SearchTextField.js'
import BoxAroundTextField from '../common/BoxAroundTextField.js'
import StandardButton from '../common/StandardButton.js'
import ClearButton from '../common/ClearButton.js'

import MemoEditDialog from './MemoEditDialog.js'
import MemoDeleteDialog from './MemoDeleteDialog.js'
import { fetchMemos, errorMessage, formatDateTime } from './memoApi.js'

LicenseInfo.setLicenseKey(
  process.env.REACT_APP_MUI_LICENSE,
);

const theme = createTheme(
  jaJP,
);

export default function Memo(props) {

  const { token } = props;

  const [backdropOpen, setBackdropOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState({
    open: false,
    type: "success",
    message: ""
  });

  const [gridRows, setGridRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [keyword, setKeyword] = useState(localStorage.getItem('savedMemoKeyword') || '');
  const [doctorCode, setDoctorCode] = useState(localStorage.getItem('savedMemoDoctorCode') || '');
  const [workplaceInstitutionCode, setWorkplaceInstitutionCode] = useState(localStorage.getItem('savedMemoWorkplaceInstitutionCode') || '');
  const [mine, setMine] = useState(localStorage.getItem('savedMemoMine') === '1');

  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    document.title = 'D.search ｜ メモ一覧';
  }, []);

  const getData = useCallback(() => {
    setAlertStatus({
      open: false,
      type: "close",
      message: ""
    });
    setBackdropOpen(true);

    fetchMemos(token, {
      keyword: keyword,
      personal_code: doctorCode,
      workplace_institution_code: workplaceInstitutionCode,
      mine: mine ? '1' : '',
    })
      .then((response) => {
        setBackdropOpen(false);
        setCount(response.data.count);
        setGridRows(response.data.rows);
      })
      .catch((error) => {
        setBackdropOpen(false);
        setAlertStatus({
          open: true,
          type: "error",
          message: errorMessage(error)
        });
      });
  }, [token, keyword, doctorCode, workplaceInstitutionCode, mine]);

  // 初回表示時は保存済みの条件で一覧を出す
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getData(); }, []);

  const handleTextChange = (setter, storageKey) => (event) => {
    localStorage.setItem(storageKey, event.target.value);
    setter(event.target.value);
  };

  const handleMineChange = (event) => {
    localStorage.setItem('savedMemoMine', event.target.checked ? '1' : '');
    setMine(event.target.checked);
  };

  const clearCondition = () => {
    setKeyword('');
    setDoctorCode('');
    setWorkplaceInstitutionCode('');
    setMine(false);

    localStorage.setItem('savedMemoKeyword', '');
    localStorage.setItem('savedMemoDoctorCode', '');
    localStorage.setItem('savedMemoWorkplaceInstitutionCode', '');
    localStorage.setItem('savedMemoMine', '');
  };

  const handleEditClose = (saved) => {
    setEditTarget(null);
    if (saved) {
      getData();
    }
  };

  const handleDeleteClose = (deleted) => {
    setDeleteTarget(null);
    if (deleted) {
      getData();
    }
  };

  const columns = [
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'updated_at',
      headerName: '更新日時',
      width: 140,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      field: 'doctor_name',
      headerName: '医師名',
      width: 130,
    },
    {
      field: 'personal_code',
      headerName: '医師コード',
      width: 100,
    },
    {
      field: 'formal_name',
      headerName: '病院正式名',
      width: 220,
    },
    {
      field: 'workplace_institution_code',
      headerName: '施設コード',
      width: 100,
    },
    {
      field: 'content',
      headerName: 'メモ',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', py: 1 }}>{params.value}</Box>
      ),
    },
    {
      field: 'author_name',
      headerName: '作成者',
      width: 120,
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => (params.row.editable &&
        <>
          <IconButton size="small" aria-label="編集" onClick={() => setEditTarget(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="削除" onClick={() => setDeleteTarget(params.row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box>
      <Navbar {...props}>
        {alertStatus.open &&
          <Alert
            onClose={() => {
              setAlertStatus({
                open: false,
                type: "close",
                message: ""
              });
            }}
            severity={alertStatus.type}>{alertStatus.message}
          </Alert>
        }
        <StandardBox>
          <HeadLine1>
            メモ一覧
          </HeadLine1>
        </StandardBox>

        <StandardBox>
          <HeadLine2>
            ■ 条件から探す
          </HeadLine2>
          <BoxAroundTextField>
            <SearchTextField
              id="memo-keyword"
              label="キーワード（メモ・医師名・病院名）"
              value={keyword}
              onChange={handleTextChange(setKeyword, 'savedMemoKeyword')}
              sx={{ width: 400 }}
            />
            <SearchTextField
              id="memo-doctor-code"
              label="医師コード"
              value={doctorCode}
              onChange={handleTextChange(setDoctorCode, 'savedMemoDoctorCode')}
            />
            <SearchTextField
              id="memo-workplace-institution-code"
              label="施設コード"
              value={workplaceInstitutionCode}
              onChange={handleTextChange(setWorkplaceInstitutionCode, 'savedMemoWorkplaceInstitutionCode')}
            />
            <FormControlLabel
              control={<Checkbox checked={mine} onChange={handleMineChange} />}
              label="自分のメモのみ"
            />
          </BoxAroundTextField>
        </StandardBox>

        <StandardBox>
          <ButtonStack>
            <StandardButton onClick={getData}>検索</StandardButton>
            <ClearButton onClick={clearCondition}>条件クリア</ClearButton>
          </ButtonStack>
        </StandardBox>

        <CountBox>
          検索件数：{count}　（メモの追加は医師検索の医師・病院詳細から行います）
        </CountBox>

        <ThemeProvider theme={theme}>
          <DataGridPro
            autoHeight
            rows={gridRows}
            columns={columns}
            density='compact'
            getRowHeight={() => 'auto'}
            pagination
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            pageSize={pageSize}
            disableSelectionOnClick={true}
          />
        </ThemeProvider>
      </Navbar>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdropOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <MemoEditDialog
        open={Boolean(editTarget)}
        onClose={handleEditClose}
        token={token}
        memo={editTarget}
      />

      <MemoDeleteDialog
        open={Boolean(deleteTarget)}
        onClose={handleDeleteClose}
        token={token}
        memo={deleteTarget}
      />
    </Box>
  )
}
